import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Link,
  IconButton,
} from '@material-ui/core';
import {
  Link as LinkIcon,
  Check as CheckIcon,
  Sort as SortIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { FallbackAvatar } from '../../../../../general/user-profile/components/Avatar';
import { GET_MATCHING_CANDIDATES, GET_APPLICANT_AVAILABILITY } from './matching-panel-model.gql';
import { MatchScoringCriterion } from './use-matching-rules';

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function calculateAge(dateOfBirth: string | null | undefined): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

interface SimpleAvailability {
  mondayAm: boolean;
  mondayPm: boolean;
  tuesdayAm: boolean;
  tuesdayPm: boolean;
  wednesdayAm: boolean;
  wednesdayPm: boolean;
  thursdayAm: boolean;
  thursdayPm: boolean;
  fridayAm: boolean;
  fridayPm: boolean;
  saturdayAm: boolean;
  saturdayPm: boolean;
  sundayAm: boolean;
  sundayPm: boolean;
}

const WEEKDAY_AM_KEYS: (keyof SimpleAvailability)[] = [
  'mondayAm',
  'tuesdayAm',
  'wednesdayAm',
  'thursdayAm',
  'fridayAm',
];
const WEEKDAY_PM_KEYS: (keyof SimpleAvailability)[] = [
  'mondayPm',
  'tuesdayPm',
  'wednesdayPm',
  'thursdayPm',
  'fridayPm',
];
const WEEKEND_KEYS: (keyof SimpleAvailability)[] = ['saturdayAm', 'saturdayPm', 'sundayAm', 'sundayPm'];
const ALL_KEYS: (keyof SimpleAvailability)[] = [...WEEKDAY_AM_KEYS, ...WEEKDAY_PM_KEYS, ...WEEKEND_KEYS];

function summariseAvailability(avail: SimpleAvailability | null | undefined): string {
  if (!avail) return 'Not set';
  const weekdayAm = WEEKDAY_AM_KEYS.some((k) => avail[k]);
  const weekdayPm = WEEKDAY_PM_KEYS.some((k) => avail[k]);
  const weekend = WEEKEND_KEYS.some((k) => avail[k]);
  const parts: string[] = [];
  if (weekdayAm) parts.push('Weekday daytime');
  if (weekdayPm) parts.push('Weekday evenings');
  if (weekend) parts.push('Weekends');
  return parts.length > 0 ? parts.join(', ') : 'Not set';
}

type AvailabilityFilter = 'weekday-daytime' | 'weekday-evenings' | 'weekends';

function matchesAvailabilityFilter(avail: SimpleAvailability | null | undefined, filter: AvailabilityFilter): boolean {
  if (!avail) return false;
  if (filter === 'weekday-daytime') return WEEKDAY_AM_KEYS.some((k) => avail[k]);
  if (filter === 'weekday-evenings') return WEEKDAY_PM_KEYS.some((k) => avail[k]);
  return WEEKEND_KEYS.some((k) => avail[k]);
}

function computeCompatibility(
  applicantAvail: SimpleAvailability | null | undefined,
  candidateAvail: SimpleAvailability | null | undefined
): number {
  if (!applicantAvail || !candidateAvail) return 0;
  const applicantSlots = ALL_KEYS.filter((k) => applicantAvail[k]).length;
  if (applicantSlots === 0) return 0;
  const overlap = ALL_KEYS.filter((k) => applicantAvail[k] && candidateAvail[k]).length;
  return overlap / applicantSlots;
}

function getCompatLevel(score: number): 1 | 2 | 3 | 4 | 5 {
  if (score >= 0.75) return 1;
  if (score >= 0.5) return 2;
  if (score >= 0.25) return 3;
  if (score > 0) return 4;
  return 5;
}

const DAY_COLUMNS: {
  key: string;
  label: string;
  am: keyof SimpleAvailability;
  pm: keyof SimpleAvailability;
}[] = [
  { key: 'mon', label: 'M', am: 'mondayAm', pm: 'mondayPm' },
  { key: 'tue', label: 'T', am: 'tuesdayAm', pm: 'tuesdayPm' },
  { key: 'wed', label: 'W', am: 'wednesdayAm', pm: 'wednesdayPm' },
  { key: 'thu', label: 'T', am: 'thursdayAm', pm: 'thursdayPm' },
  { key: 'fri', label: 'F', am: 'fridayAm', pm: 'fridayPm' },
  { key: 'sat', label: 'S', am: 'saturdayAm', pm: 'saturdayPm' },
  { key: 'sun', label: 'S', am: 'sundayAm', pm: 'sundayPm' },
];

const COMPAT_COLORS: Record<number, string> = {
  1: '#2E7D32',
  2: '#7CB342',
  3: '#FBC02D',
  4: '#F57C00',
  5: '#D32F2F',
};

interface MatchingCandidateApplication {
  applicationId: string;
  name: string;
  email: string;
  status: string;
  profile: {
    profileId: string;
    preferredName: string;
    lastName: string;
    dateOfBirth: string | null;
    programs: { programId: string; name: string }[] | null;
    simpleAvailability: SimpleAvailability | null;
  } | null;
  applicationRoles: Array<{
    applicationRoleId: string;
    role: {
      activityRoleId: string;
      name: string;
    };
    status: string;
  }>;
  funnel: {
    name: string;
  } | null;
}

const useMatchingStyles = () => {
  const { theme, isMobile } = useCampfireTheme();
  const sidePadding = isMobile ? 14 : 18;
  return makeStyles({
    groupHeader: {
      boxSizing: 'border-box',
      width: '100%',
      padding: `0 ${sidePadding}px`,
    },
    groupHeaderText: {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.color.grey.neutralBrand800,
      display: 'flex',
      alignItems: 'center',
      padding: '.6em 0',
    },
    subtitle: {
      display: 'block',
      padding: `0 ${sidePadding}px`,
      marginBottom: 8,
    },
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap' as const,
      padding: `0 ${sidePadding}px`,
      marginBottom: 10,
    },
    filterChip: {
      height: 24,
      fontSize: '0.75rem',
      fontWeight: 550,
      borderRadius: 4,
    },
    stack: {
      boxSizing: 'border-box',
      width: '100%',
      padding: isMobile ? '0 12px 12px' : '0 18px 16px',
    },
    group: {
      boxSizing: 'border-box',
      width: '100%',
      backgroundColor: '#ffffff',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0px 1px 2px rgba(114, 114, 114, 0.08), 0px 1px 3px rgba(214, 214, 214, 0.18)',
    },
    candidateItem: {
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.color.grey.border}`,
      },
    },
    candidateRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: `12px ${sidePadding}px`,
      cursor: 'pointer',
      transition: 'background-color 120ms ease',
      '&:hover': {
        backgroundColor: theme.color.grey.background,
      },
    },
    candidateMain: {
      flex: 1,
      minWidth: 0,
    },
    candidateNameRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap' as const,
      marginBottom: 2,
    },
    tag: {
      padding: '3px 5px',
      borderRadius: 3,
      fontWeight: 600,
      fontSize: 11,
      lineHeight: 1.2,
      color: '#444',
      backgroundColor: '#e7e7e7',
    },
    tagOutlined: {
      padding: '2px 5px',
      borderRadius: 3,
      fontWeight: 600,
      fontSize: 11,
      lineHeight: 1.2,
      color: theme.color.grey.neutral400,
      border: `1px solid ${theme.color.grey.border}`,
    },
    compatDot: {
      width: 14,
      height: 14,
      borderRadius: '50%',
      flexShrink: 0,
    },
    candidateRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexShrink: 0,
    },
    matchBtn: {
      textTransform: 'none' as const,
      fontWeight: 600,
      fontSize: 13,
      borderRadius: 4,
      padding: '6px 14px',
      minWidth: 'auto',
      boxShadow: 'none',
    },
    matchedBtn: {
      backgroundColor: theme.status.green.medium,
      color: '#FFF',
      '&:hover': {
        backgroundColor: theme.status.green.dark,
      },
    },
    detailPanel: {
      padding: `14px ${sidePadding}px 16px`,
      borderTop: `1px solid ${theme.color.grey.border}`,
      backgroundColor: theme.color.grey.background,
    },
    detailColumns: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '20px 40px',
      alignItems: 'flex-start',
    },
    detailCol: {
      flex: '1 1 240px',
      minWidth: 0,
    },
    detailHeading: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.color.grey.neutral500,
      marginBottom: 8,
    },
    detailList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    detailLabel: {
      display: 'inline-block',
      minWidth: 70,
      color: theme.color.grey.neutral300,
    },
    detailLink: {
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 13,
      fontWeight: 500,
      marginTop: 10,
    },
    gridRow: {
      display: 'grid',
      gridTemplateColumns: '30px repeat(7, minmax(0, 1fr))',
      gap: '3px',
      alignItems: 'center',
      marginBottom: 3,
      maxWidth: 360,
    },
    gridLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      textAlign: 'center' as const,
    },
    gridCell: {
      height: 14,
      borderRadius: 2,
    },
    legendRow: {
      display: 'flex',
      gap: '12px',
      marginTop: 8,
      flexWrap: 'wrap' as const,
    },
    legendItem: {
      fontSize: 12,
      color: theme.color.grey.neutral300,
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    legendSwatch: {
      width: 10,
      height: 10,
      borderRadius: 2,
    },
    emptyState: {
      padding: `16px ${sidePadding}px`,
    },
  })();
};

export interface MatchedCandidate {
  applicationId: string;
  name: string;
  profileId: string;
  preferredName: string;
  lastName: string;
  roleName: string;
  roleId: string;
  programs: { programId: string; name: string }[];
}

interface MatchingPanelProps {
  applicantName: string;
  applicantRoleName: string;
  applicantProfileId: string;
  counterpartRoleId: string;
  counterpartRoleName: string;
  /** The rule's program, or null when the rule pairs within the applicant's own programs. */
  ruleProgramId: string | null;
  scoringCriteria: MatchScoringCriterion[];
  minimumScore?: number;
  onMatchSelect?: (candidate: MatchedCandidate | null) => void;
}

export const MatchingPanel: React.FC<MatchingPanelProps> = ({
  applicantName,
  applicantRoleName,
  applicantProfileId,
  counterpartRoleId,
  counterpartRoleName,
  ruleProgramId,
  scoringCriteria,
  minimumScore = 0,
  onMatchSelect,
}) => {
  const classes = useMatchingStyles();
  const { theme } = useCampfireTheme();
  const [matchedId, setMatchedId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<AvailabilityFilter[]>([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: applicantData, loading: applicantLoading } = useCampfireQuery<any, any>(GET_APPLICANT_AVAILABILITY, {
    options: {
      variables: { profileId: applicantProfileId },
    },
  });

  const applicantAvailability: SimpleAvailability | null = applicantData?.vm?.profile?.simpleAvailability ?? null;
  const applicantPrograms: { programId: string; name: string }[] = applicantData?.vm?.profile?.programs ?? [];

  const programIds = useMemo(
    () => (ruleProgramId ? [ruleProgramId] : applicantPrograms.map((program) => program.programId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ruleProgramId, JSON.stringify(applicantPrograms)]
  );

  const { data, loading } = useCampfireQuery<any, any>(GET_MATCHING_CANDIDATES, {
    options: {
      variables: { roleId: counterpartRoleId ?? '', programIds },
      skip: !counterpartRoleId || programIds.length === 0,
    },
  });

  const candidates: MatchingCandidateApplication[] = useMemo(
    () =>
      ((data?.vm?.matchingCandidates ?? []) as MatchingCandidateApplication[]).filter(
        (candidate, index, all) =>
          candidate.profile?.profileId !== applicantProfileId &&
          all.findIndex((other) => other.applicationId === candidate.applicationId) === index
      ),
    [data, applicantProfileId]
  );

  const isScored = scoringCriteria.length > 0;
  const showAvailability = scoringCriteria.includes('availability');

  const scoreCandidate = (candidate: MatchingCandidateApplication): number | null => {
    if (!isScored) return null;
    const scores = scoringCriteria.map((criterion) => {
      switch (criterion) {
        case 'availability':
          return computeCompatibility(applicantAvailability, candidate.profile?.simpleAvailability);
        default:
          return 0;
      }
    });
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  const filteredAndSorted = useMemo(() => {
    let result = candidates.filter((c) => {
      const score = scoreCandidate(c);
      if (score !== null && score * 100 < minimumScore) return false;
      if (activeFilters.length === 0) return true;
      const avail = c.profile?.simpleAvailability;
      return activeFilters.every((f) => matchesAvailabilityFilter(avail, f));
    });

    if (isScored) {
      result = [...result].sort((a, b) => {
        const scoreA = scoreCandidate(a) ?? 0;
        const scoreB = scoreCandidate(b) ?? 0;
        return sortAsc ? scoreA - scoreB : scoreB - scoreA;
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, activeFilters, applicantAvailability, sortAsc, minimumScore, scoringCriteria.join()]);

  const handleMatch = (candidate: MatchingCandidateApplication) => {
    const isAlreadyMatched = matchedId === candidate.applicationId;
    const newId = isAlreadyMatched ? null : candidate.applicationId;
    setMatchedId(newId);

    if (onMatchSelect) {
      if (isAlreadyMatched || !candidate.profile) {
        onMatchSelect(null);
      } else {
        const roleName =
          candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
          'Applicant';
        onMatchSelect({
          applicationId: candidate.applicationId,
          name: `${candidate.profile.preferredName} ${candidate.profile.lastName}`,
          profileId: candidate.profile.profileId,
          preferredName: candidate.profile.preferredName,
          lastName: candidate.profile.lastName,
          roleName,
          roleId: counterpartRoleId ?? '',
          programs: candidate.profile.programs ?? [],
        });
      }
    }
  };

  const toggleExpanded = (applicationId: string) => {
    const next = expandedId === applicationId ? null : applicationId;
    setExpandedId(next);

    if (next) {
      window.setTimeout(() => {
        const panel = document.getElementById(`match-detail-${applicationId}`);

        if (panel) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 0);
    }
  };

  const toggleFilter = (filter: AvailabilityFilter) => {
    setActiveFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  };

  const availableFilters: { key: AvailabilityFilter; label: string }[] = [
    { key: 'weekday-daytime', label: 'Weekday daytime' },
    { key: 'weekday-evenings', label: 'Weekday evenings' },
    { key: 'weekends', label: 'Weekends' },
  ];

  if (!counterpartRoleId) {
    return null;
  }

  const remainingFilters = availableFilters.filter((f) => !activeFilters.includes(f.key));

  return (
    <Box width={1}>
      <Box display='flex' alignItems='center' justifyContent='space-between' className={classes.groupHeader}>
        <Typography className={classes.groupHeaderText}>Find a Match</Typography>
        {isScored && (
          <TabletButton
            variant='text'
            color='primary'
            size='small'
            startIcon={<SortIcon />}
            onClick={() => setSortAsc((prev) => !prev)}
          >
            {sortAsc ? 'Lowest match first' : 'Highest match first'}
          </TabletButton>
        )}
      </Box>

      {loading || applicantLoading ? (
        <Box padding={3} display='flex' justifyContent='center'>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Typography variant='caption' color='textSecondary' className={classes.subtitle}>
            {`${counterpartRoleName} candidates for ${applicantName} (${applicantRoleName}). Showing ${filteredAndSorted.length} of ${candidates.length}.`}
          </Typography>

          <Box className={classes.filterBar}>
            {activeFilters.map((filter) => (
              <Chip
                key={filter}
                label={availableFilters.find((f) => f.key === filter)?.label ?? filter}
                size='small'
                color='primary'
                variant='outlined'
                className={classes.filterChip}
                onDelete={() => toggleFilter(filter)}
              />
            ))}
            {remainingFilters.length > 0 && (
              <TabletButton
                variant='text'
                color='primary'
                size='small'
                startIcon={<AddIcon />}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setFilterMenuAnchor(e.currentTarget)}
              >
                Filter
              </TabletButton>
            )}
            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
            >
              {remainingFilters.map((f) => (
                <MenuItem
                  key={f.key}
                  onClick={() => {
                    toggleFilter(f.key);
                    setFilterMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <ScheduleIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText primary={f.label} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box className={classes.stack}>
            <Box className={classes.group}>
              {filteredAndSorted.map((candidate) => {
                const isMatched = matchedId === candidate.applicationId;
                const isExpanded = expandedId === candidate.applicationId;
                const age = calculateAge(candidate.profile?.dateOfBirth);
                const displayName = candidate.profile
                  ? `${candidate.profile.preferredName} ${candidate.profile.lastName}`
                  : candidate.name;
                const roleName =
                  candidate.applicationRoles.find((ar) => ar.role.activityRoleId === counterpartRoleId)?.role.name ??
                  'Applicant';
                const availSummary = summariseAvailability(candidate.profile?.simpleAvailability);
                const compatScore = scoreCandidate(candidate);
                const candidateAvail = candidate.profile?.simpleAvailability;
                const subText = [age !== null ? `Age ${age}` : null, availSummary].filter(Boolean).join(' · ');

                return (
                  <Box key={candidate.applicationId} className={classes.candidateItem}>
                    <Box className={classes.candidateRow} onClick={() => toggleExpanded(candidate.applicationId)}>
                      <FallbackAvatar initials={getInitials(displayName)} size={32} />

                      <Box className={classes.candidateMain}>
                        <Box className={classes.candidateNameRow}>
                          <Typography variant='body2'>{displayName}</Typography>
                          <span className={classes.tag}>{roleName}</span>
                          {candidate.funnel && <span className={classes.tagOutlined}>{candidate.funnel.name}</span>}
                        </Box>
                        <Typography variant='caption' color='textSecondary'>
                          {subText}
                        </Typography>
                      </Box>

                      <Box className={classes.candidateRight}>
                        {compatScore !== null && (
                          <Box
                            className={classes.compatDot}
                            style={{ backgroundColor: COMPAT_COLORS[getCompatLevel(compatScore)] }}
                            title={`Compatibility: ${Math.round(compatScore * 100)}%`}
                          />
                        )}
                        <Button
                          variant='contained'
                          color={isMatched ? 'default' : 'primary'}
                          className={isMatched ? `${classes.matchBtn} ${classes.matchedBtn}` : classes.matchBtn}
                          disableElevation
                          startIcon={
                            isMatched ? <CheckIcon style={{ fontSize: 17 }} /> : <LinkIcon style={{ fontSize: 17 }} />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMatch(candidate);
                          }}
                        >
                          {isMatched ? 'Matched' : 'Match'}
                        </Button>
                        <IconButton
                          size='small'
                          aria-label={isExpanded ? 'Hide details' : 'Show details'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(candidate.applicationId);
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
                        </IconButton>
                      </Box>
                    </Box>

                    {isExpanded && (
                      <Box className={classes.detailPanel} id={`match-detail-${candidate.applicationId}`}>
                        <Box className={classes.detailColumns}>
                          <Box className={classes.detailCol}>
                            <Typography className={classes.detailHeading}>Details</Typography>
                            <Box className={classes.detailList}>
                              {candidate.email && (
                                <Typography variant='body2'>
                                  <span className={classes.detailLabel}>Email</span>
                                  {candidate.email}
                                </Typography>
                              )}
                              <Typography variant='body2'>
                                <span className={classes.detailLabel}>Role</span>
                                {roleName}
                              </Typography>
                              {candidate.funnel && (
                                <Typography variant='body2'>
                                  <span className={classes.detailLabel}>Source</span>
                                  {candidate.funnel.name}
                                </Typography>
                              )}
                              {(candidate.profile?.programs ?? []).length > 0 && (
                                <Typography variant='body2'>
                                  <span className={classes.detailLabel}>Programs</span>
                                  {(candidate.profile?.programs ?? []).map((program) => program.name).join(', ')}
                                </Typography>
                              )}
                              <Typography variant='body2'>
                                <span className={classes.detailLabel}>Status</span>
                                {candidate.status}
                              </Typography>
                            </Box>

                            <Link
                              className={classes.detailLink}
                              href={
                                candidate.profile
                                  ? `/management/volunteers/incoming-volunteers?volunteerProfileId=${candidate.profile.profileId}`
                                  : `/management/volunteers/incoming-volunteers?applicationId=${candidate.applicationId}`
                              }
                              target='_blank'
                              rel='noopener noreferrer'
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              Open full application
                              <OpenInNewIcon style={{ fontSize: 14, marginLeft: 4 }} />
                            </Link>
                          </Box>

                          {showAvailability && (
                            <Box className={classes.detailCol}>
                              <Typography className={classes.detailHeading}>Availability overlap</Typography>
                              <Box className={classes.gridRow}>
                                <span />
                                {DAY_COLUMNS.map((day) => (
                                  <span key={day.key} className={classes.gridLabel}>
                                    {day.label}
                                  </span>
                                ))}
                              </Box>
                              {(['am', 'pm'] as const).map((slot) => (
                                <Box key={slot} className={classes.gridRow}>
                                  <span className={classes.gridLabel}>{slot.toUpperCase()}</span>
                                  {DAY_COLUMNS.map((day) => {
                                    const key = day[slot];
                                    const applicantHas = Boolean(applicantAvailability?.[key]);
                                    const candidateHas = Boolean(candidateAvail?.[key]);
                                    const background =
                                      applicantHas && candidateHas
                                        ? theme.status.green.medium
                                        : applicantHas
                                        ? '#CDDCF3'
                                        : candidateHas
                                        ? '#DCE9E1'
                                        : theme.color.grey.border;

                                    return (
                                      <span
                                        key={`${slot}-${day.key}`}
                                        className={classes.gridCell}
                                        style={{ backgroundColor: background }}
                                      />
                                    );
                                  })}
                                </Box>
                              ))}
                              <Box className={classes.legendRow}>
                                <span className={classes.legendItem}>
                                  <span
                                    className={classes.legendSwatch}
                                    style={{ backgroundColor: theme.status.green.medium }}
                                  />
                                  Both
                                </span>
                                <span className={classes.legendItem}>
                                  <span className={classes.legendSwatch} style={{ backgroundColor: '#CDDCF3' }} />
                                  {`${applicantName} only`}
                                </span>
                                <span className={classes.legendItem}>
                                  <span className={classes.legendSwatch} style={{ backgroundColor: '#DCE9E1' }} />
                                  {`${displayName} only`}
                                </span>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
              {filteredAndSorted.length === 0 && (
                <Box className={classes.emptyState}>
                  <Typography variant='body2' color='textSecondary'>
                    {candidates.length === 0
                      ? `No ${counterpartRoleName} available to match with in this program yet.`
                      : 'No candidates match the current filters.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
